// Pure helpers for the user account-lifecycle shown in Access Management. Mirrors the
// People Management access-management lifecycle, adapted to the Super Admin design tokens.
// Kept free of React/MUI so it can be unit tested in isolation.
//
// The grid opens on every status so newly-added (pending) users are visible, and each row
// carries a colour-coded status pill and a status-aware action (e.g. Reactivate for a
// deleted account), matching the legacy access-control screen.

// Identity UserStatus values (mirror of Identity.Domain UserStatus).
export const ACCOUNT_STATUS = {
  Active: 'Active',
  Inactive: 'Inactive',
  Suspended: 'Suspended',
  Deleted: 'Deleted',
  PendingVerification: 'PendingVerification',
};

// The sentinel for "don't filter by status" (every status listed together).
export const ALL_STATUSES = 'All';

// The default filter — the grid opens on all statuses so newly-invited (pending) users and
// deleted/reactivatable accounts are visible without changing the filter.
export const DEFAULT_STATUS_FILTER = ALL_STATUSES;

// Options for the header status filter. Active first (the default), All last.
export const STATUS_FILTER_OPTIONS = [
  { value: ACCOUNT_STATUS.Active, label: 'Active' },
  { value: ACCOUNT_STATUS.PendingVerification, label: 'Pending activation' },
  { value: ACCOUNT_STATUS.Inactive, label: 'Inactive' },
  { value: ACCOUNT_STATUS.Suspended, label: 'Suspended' },
  { value: ACCOUNT_STATUS.Deleted, label: 'Deleted' },
  { value: ALL_STATUSES, label: 'All statuses' },
];

// Whether the lifecycle affordances (status pill + status-aware labels) should show. Always
// on now — every row shows its status pill and status-aware action regardless of the filter.
export const showsLifecycle = (_statusFilter) => true;

// Colour-coded pill presentation for a status, on the Super Admin palette. `inviteExpired`
// turns a still-pending account red ("Invite expired") instead of amber.
export const accountStatusDisplay = (status, inviteExpired = false) => {
  switch (status) {
    case ACCOUNT_STATUS.Active:
      return { label: 'Active', bg: '#e1f5ee', fg: '#0f6e56' };
    case ACCOUNT_STATUS.PendingVerification:
      return inviteExpired
        ? { label: 'Invite expired', bg: '#fcebeb', fg: '#a32d2d' }
        : { label: 'Awaiting password', bg: '#faeeda', fg: '#854f0b' };
    case ACCOUNT_STATUS.Suspended:
      return { label: 'Suspended', bg: '#fcebeb', fg: '#a32d2d' };
    case ACCOUNT_STATUS.Inactive:
      return { label: 'Inactive', bg: '#f1efe8', fg: '#5f5e5a' };
    case ACCOUNT_STATUS.Deleted:
      return { label: 'Deleted', bg: '#f1efe8', fg: '#5f5e5a' };
    default:
      return { label: status || 'Unknown', bg: '#f1efe8', fg: '#5f5e5a' };
  }
};

// The primary lifecycle action for a status, and its status-appropriate label:
//  - Active               -> Reset Password action, "Send password reset"
//  - PendingVerification  -> Reset Password action, "Resend set-password link"
//  - Inactive/Suspended/Deleted -> Reactivate action, "Send activation request"
// The action KEYS map to the existing account-action handlers; only the label is
// status-aware. Returns null on the Active default so nothing changes there.
export const lifecycleAction = (status) => {
  switch (status) {
    case ACCOUNT_STATUS.PendingVerification:
      return { key: 'resetPassword', label: 'Resend set-password link' };
    case ACCOUNT_STATUS.Inactive:
    case ACCOUNT_STATUS.Suspended:
    case ACCOUNT_STATUS.Deleted:
      return { key: 'reactivate', label: 'Send activation request' };
    case ACCOUNT_STATUS.Active:
    default:
      return { key: 'resetPassword', label: 'Send password reset' };
  }
};
